import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Layout from "./components/layout/Layout";
import DataMigration from "./components/DataMigration";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { SyncStatusIndicator } from "./components/ui/SyncStatusIndicator";

// Lazy load pages to improve initial loading
const FastingPage = lazy(() => import("./pages/FastingPage"));
const LogPage = lazy(() => import("./pages/LogPage"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const FriendsPage = lazy(() => import("./pages/FriendsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const DocumentationPage = lazy(() => import("./pages/DocumentationPage"));

// Create a RedirectHandler component to handle the root path
const RedirectHandler = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!loading) {
      if (isAuthenticated) {
        navigate('/log', { replace: true });
      } else {
        navigate('/landing', { replace: true });
      }
    }
  }, [isAuthenticated, loading, navigate]);
  
  return <LoadingFallback />;
};

const LoadingFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="text-center">
      <h1 className="text-2xl font-bold mb-2">Loading...</h1>
      <div className="mt-4 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30000,
    },
  },
});

const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LoadingFallback />;
  }
  
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Root path handler - uses the redirect component */}
        <Route path="/" element={<RedirectHandler />} />
        
        {/* Public routes */}
        <Route path="/login" element={isAuthenticated ? <Navigate to="/log" replace /> : <LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/landing" element={isAuthenticated ? <Navigate to="/log" replace /> : <LandingPage />} />
        <Route path="/docs" element={<DocumentationPage />} />
        
        {/* Protected routes wrapped in Layout */}
        <Route element={<Layout />}>
          <Route path="/log" element={
            isAuthenticated ? <LogPage /> : <Navigate to="/login" replace />
          } />
          
          <Route path="/fasting" element={
            isAuthenticated ? <FastingPage /> : <Navigate to="/login" replace />
          } />
          
          <Route path="/calendar" element={
            isAuthenticated ? <CalendarPage /> : <Navigate to="/login" replace />
          } />
          
          <Route path="/settings" element={
            isAuthenticated ? <SettingsPage /> : <Navigate to="/login" replace />
          } />
          
          <Route path="/friends" element={
            isAuthenticated ? <FriendsPage /> : <Navigate to="/login" replace />
          } />
        </Route>
        
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

// Reorder providers to fix routing and authentication state issues
const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <TooltipProvider>
        <AuthProvider>
          <Toaster />
          <Sonner />
          <DataMigration />
          <SyncStatusIndicator />
          <PWAInstallPrompt />
          <AppRoutes />
        </AuthProvider>
      </TooltipProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
